// src/domains/shell.ts —— 外壳原生（06 A-01 登录 / A-01b 首登强改密 / A-02 全局框架）；modal 合成 = 外壳子键 + 各域 modal()
import { ApiError } from '../../../shared/src/api/client';
import type { AgentLoginResp, PublicConfig } from '../../../shared/src/api/types';
import { interpolate } from '../../../shared/src/errors';
import { LOGIN_REASONS, emptyFcp } from '../gp';
import { I18N_AGENT_ERRORS } from '../i18n/errors';
import { I18N_AGENT_SHELL, REWRITES_AGENT_SHELL } from '../i18n/shell';
import type { AgentState, FcpDraft, Page } from '../state';
import { navStyle } from '../ui';
import { homeOf } from './home';
import { KEYS, MODAL_KEYS } from './shell.keys';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';

// 07 §3 / X-24：8–64 位且同时含字母与数字（与后端 pwd.ValidatePolicy 同口径）
const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/;
// 0045：「¥ 我的售价」排在概览之后（06 A-10）；2026-10-21：「✉ 推广推送」为末项（06 A-11）
const NAV: ReadonlyArray<readonly [Page, string]> = [['home', '◫'], ['prices', '¥'], ['subs', '♛'], ['users', '◉'], ['spend', '≡'], ['commissions', '％'], ['withdraw', '↑'], ['broadcast', '✉']];
const TABLE_PAGES: readonly Page[] = ['subs', 'users', 'spend', 'commissions'];

function setSh(ctx: AgentCtx, patch: Partial<AgentState['sh']>): void {
  ctx.host.setState({ sh: { ...ctx.host.state.sh, ...patch } });
}

function patchFcp(ctx: AgentCtx, patch: Partial<FcpDraft>): void {
  const cur = ctx.host.state.sh.fcp;
  if (cur) setSh(ctx, { fcp: { ...cur, ...patch } });
}

// TD-822：忽略 props.startLoggedIn。持久 token → /me；否则试探 refresh cookie；均失败 → 登录页。
// 期间 screen=booting（不渲染登录表单，无闪屏、无并发手动登录）；established 成功即离开 booting
async function boot(ctx: AgentCtx): Promise<void> {
  const g = ctx.gp;
  try {
    setSh(ctx, { config: await g.api.call<PublicConfig>('GET /api/public/config') });
  } catch (e) {
    g.ui.toast(g.ui.err(e));
  }
  // 上次会话关页期间已空闲超时：直接落登录页并提示（2026-10-15）
  if (await g.session.bootIdle()) {
    if (ctx.host.state.screen === 'booting') ctx.host.setState({ screen: 'login' });
    return;
  }
  const ok = !!g.tokens.get() || (await g.api.tryRefresh());
  const token = g.tokens.get();
  if (ok && token) {
    try {
      await g.session.established(token, { persist: true, restricted: false, reason: 'boot' });
      return;
    } catch (e) {
      console.warn('[gp:agent] 会话恢复失败', e);
    }
  }
  // 只从 booting 落到登录页：会话恢复已进壳 / 进 FCP，或期间 session:lost(expired) 已切登录页时不再改屏
  // refresh 带原因失败（空闲 / 超过最长在线）：登录页提示原因
  const notice = ok ? '' : g.session.bootNotice();
  if (ctx.host.state.screen === 'booting') ctx.host.setState({ screen: 'login', ...(notice ? { sh: { ...ctx.host.state.sh, loginErr: notice } } : {}) });
}

async function doLogin(ctx: AgentCtx): Promise<void> {
  const sh = ctx.host.state.sh;
  const t = ctx.t;
  // 登录表单只在 login 屏渲染；booting / app 屏不受理（防与 boot 的会话恢复并发 established）
  if (ctx.host.state.screen !== 'login') return;
  if (!sh.loginUser.trim() || !sh.loginPwd) return setSh(ctx, { loginErr: str(t, 'err1001') });
  await ctx.gp.ui.run(async () => {
    try {
      const r = await ctx.gp.api.call<AgentLoginResp>('POST /api/public/agent/login', { body: { account: sh.loginUser.trim(), password: sh.loginPwd } });
      const restricted = r.restricted || r.must_change_password;
      setSh(ctx, { loginPwd: '', loginErr: '' });
      // TD-811 / S-34：受限窗口 token 只存内存（不签发 refresh，刷新页面需重新登录）
      await ctx.gp.session.established(r.token, { persist: !restricted, restricted, reason: 'login' });
      if (!restricted) ctx.gp.ui.toast(str(t, 'toastLogin'));
    } catch (e) {
      setSh(ctx, { loginErr: ctx.gp.ui.err(e) });
    }
  });
}

async function submitFcp(ctx: AgentCtx): Promise<void> {
  const f = ctx.host.state.sh.fcp;
  const t = ctx.t;
  if (!f) return;
  if (!f.old) return patchFcp(ctx, { err: str(t, 'errOldPwd') });
  if (!PWD_RE.test(f.nw) || f.nw !== f.nw2 || f.nw === f.old) return patchFcp(ctx, { err: str(t, 'errPwdRule') });
  await ctx.gp.ui.run(async () => {
    try {
      await ctx.gp.api.call('PUT /api/agent/password', { body: { old: f.old, new: f.nw } });
      // 后端改密即吊销本人全部会话：清内存 token 回登录页重新登录（06 A-01b / S-97）
      ctx.gp.session.passwordChanged();
    } catch (e) {
      patchFcp(ctx, { err: e instanceof ApiError && e.code === 1001 ? str(t, 'errOldPwd') : ctx.gp.ui.err(e) });
    }
  });
}

// 表格复位由 table 域在 screen 事件内完成（A-00「导航切换」）
function go(ctx: AgentCtx, page: Page): void {
  ctx.host.setState({ page, modal: null });
}

export function createShell(features: () => readonly AgentDomain[]): AgentDomain {
  return {
    name: 'shell',
    keys: KEYS,
    modalKeys: MODAL_KEYS,
    migrated: true,
    i18n: { zh: { ...I18N_AGENT_ERRORS.zh, ...I18N_AGENT_SHELL.zh }, en: { ...I18N_AGENT_ERRORS.en, ...I18N_AGENT_SHELL.en } },
    rewrites: REWRITES_AGENT_SHELL,
    mount(ctx) {
      // session:lost 可能被并发 401 多次触发：只做幂等的切屏与清理，不发受保护请求
      ctx.events.on('session:lost', ({ reason }) => {
        if (!LOGIN_REASONS.includes(reason)) return;
        const s = ctx.host.state;
        if (s.screen === 'login' && !s.sh.me && !s.sh.fcp) return;
        ctx.host.setState({ screen: 'login', modal: null, sh: { ...s.sh, me: null, fcp: null, idleLeft: 0 } });
      });
      void boot(ctx);
    },
    unmount(ctx) {
      ctx.gp.session.dispose(); // 空闲计时与跨标签页通道
    },
    update(ctx, prev) {
      if (prev.page !== ctx.s.page && ctx.s.screen === 'app') {
        ctx.events.emit('screen', { from: prev.page, to: ctx.s.page });
        ctx.gp.session.reloadMe().catch((e: unknown) => console.warn('[gp:agent] 刷新 /me 失败', e));
      }
    },
    vals(ctx) {
      const { s, t, lang } = ctx;
      const sh = s.sh;
      const ho = homeOf(s);
      const me = sh.me;
      const m = s.modal;
      const fcp = sh.fcp ?? emptyFcp();
      const modal = features()
        .map((d) => d.modal?.(ctx) ?? {})
        .reduce<Record<string, unknown>>((acc, part) => ({ ...acc, ...part }), { kind: m?.kind ?? str(t, 'newSub'), title: m?.title ?? str(t, 'newSub') });
      return {
        t,
        isBooting: s.screen === 'booting',
        isLogin: s.screen === 'login',
        isFcp: s.screen === 'fcp',
        isApp: s.screen === 'app',
        loginUser: sh.loginUser,
        onLoginUser: (e: InputEvent) => setSh(ctx, { loginUser: e.target.value, loginErr: '' }),
        loginPwd: sh.loginPwd,
        onLoginPwd: (e: InputEvent) => setSh(ctx, { loginPwd: e.target.value, loginErr: '' }),
        loginErr: sh.loginErr,
        busyLogin: s.saving && s.screen === 'login',
        doLogin: () => void doLogin(ctx),
        logout: () => void ctx.gp.session.logout(),
        setZh: () => ctx.host.setState({ lang: 'zh' }),
        setEn: () => ctx.host.setState({ lang: 'en' }),
        toggleLang: () => ctx.host.setState({ lang: lang === 'zh' ? 'en' : 'zh' }),
        langLabel: lang === 'zh' ? 'EN' : '中文',
        zhBtn: `border:0;background:transparent;cursor:pointer;font-weight:700;color:${lang === 'zh' ? '#0d9a68' : '#9aa5a1'}`,
        enBtn: `border:0;background:transparent;cursor:pointer;font-weight:700;color:${lang === 'en' ? '#0d9a68' : '#9aa5a1'}`,
        nav: NAV.map(([page, icon]) => ({ label: str(t, page), icon, badge: page === 'subs' ? ho.activeSubs : 0, go: () => go(ctx, page), style: navStyle(s.page === page) })),
        pageTitle: str(t, s.page),
        pageHint: str(t, 'h' + s.page.charAt(0).toUpperCase() + s.page.slice(1)),
        pg: { home: s.page === 'home', prices: s.page === 'prices', table: TABLE_PAGES.includes(s.page), withdraw: s.page === 'withdraw', broadcast: s.page === 'broadcast' },
        me: {
          name: me?.name ?? '',
          id: me?.biz_no ?? '',
          code: ho.links?.code ?? '',
        },
        // 改密表单的复位由 subs 域 update 负责（modal 进入 myPwd 时）
        openMyPwd: () => ctx.host.setState({ modal: { type: 'myPwd', kind: str(t, 'portal'), title: str(t, 'myPwd') } }),
        // 空闲自动退出提示框（页面内，不用 window.confirm）：倒计时期间只有点「继续使用」才续期
        idle: {
          open: s.screen === 'app' && sh.idleLeft > 0,
          title: str(t, 'idleTitle'),
          text: interpolate(str(t, 'idleWarn'), { n: sh.idleLeft }),
          btn: str(t, 'idleContinue'),
          go: () => void ctx.gp.session.continueIdle(),
        },
        modalOpen: !!m,
        closeModal: () => ctx.host.setState({ modal: null }),
        modal,
        saving: s.saving,
        toast: s.toast,
        // 新增文案经 vals 下发（模板只直引原型 t.* 键，split 双语守卫）
        fcp: { old: fcp.old, nw: fcp.nw, nw2: fcp.nw2, title: str(t, 'fcpTitle'), sub: str(t, 'fcpSub'), submit: str(t, 'fcpSubmit'), ph: str(t, 'fcpPwdPh') },
        onFcp: {
          old: (e: InputEvent) => patchFcp(ctx, { old: e.target.value, err: '' }),
          nw: (e: InputEvent) => patchFcp(ctx, { nw: e.target.value, err: '' }),
          nw2: (e: InputEvent) => patchFcp(ctx, { nw2: e.target.value, err: '' }),
        },
        fcpErr: fcp.err,
        fcpSubmit: () => void submitFcp(ctx),
        busyFcp: s.saving && s.screen === 'fcp',
      };
    },
  };
}
